import type {
  CreateScheduleOverrideRequest,
  EventTypeSlug,
  OverrideKind,
  OverrideStatus,
  ProjectionStatus,
  ScheduleOverrideSummary,
} from "../scheduleOverridesApi"

// ── Decoding the numeric enums ───────────────────────────────────────

export const KIND = {
  outreachAssignment: 0,
  outOfOffice: 1,
  siteClosure: 2,
  eventTypeOverride: 3,
} as const satisfies Record<string, OverrideKind>

export const KIND_LABELS: Record<OverrideKind, string> = {
  0: "Temporary site reassignment",
  1: "Out of office",
  2: "Site closure",
  3: "Event type override",
}

export const KIND_DESCRIPTIONS: Record<OverrideKind, string> = {
  0: "A host is moved to another site they are a member of for the window — open there, closed at every other site.",
  1: "A host is away. Closes their own hours at every site they host, or one you pick.",
  2: "A site is closed. Closes intake, off-slot and private for everyone there.",
  3: "Run one event type in place of the others — for a whole site, or one host.",
}

export const STATUS_LABELS: Record<OverrideStatus, string> = {
  0: "Pending",
  1: "Applied",
  2: "Partly applied",
  3: "Withdrawn",
  4: "Partly withdrawn",
  5: "Failed",
}

export const PROJECTION_STATUS_LABELS: Record<ProjectionStatus, string> = {
  0: "Not written",
  1: "Applied",
  2: "Failed",
  3: "Removed",
  4: "Changed since",
}

export const EVENT_TYPE_LABELS: Record<EventTypeSlug, string> = {
  intake: "Intake",
  "off-slot": "Off-slot",
  private: "Private",
}

/**
 * What an event-type override can run, in the order the dialog offers it. Private (AB#779) has no
 * hours under the legacy site-hours table, so until the slot-type table is in force (AB#707) the
 * server refuses forcing it, and says so through the preview like any other refusal.
 */
export const EVENT_TYPE_CHOICES: { value: EventTypeSlug; label: string }[] = [
  { value: "intake", label: "Intake only — off-slot and private closed" },
  { value: "off-slot", label: "Off-slot only — intake and private closed" },
  { value: "private", label: "Private only — intake and off-slot closed" },
]

/**
 * The event types an event-type override can run, named for the slot types the availability grid
 * draws (AB#806): intake is Public, private is Private, off-slot (managers only) is Managed.
 */
export const SLOT_TYPE_EVENT_CHOICES: { value: EventTypeSlug; label: string }[] = [
  { value: "intake", label: "Public" },
  { value: "private", label: "Private" },
  { value: "off-slot", label: "Managed" },
]

/** The longest note the server accepts (ScheduleOverridePlanner.MaxNoteLength). */
export const NOTE_MAX_LENGTH = 200

/**
 * How the Schedule Overrides page words a temporary site reassignment (kind 0). The server plans it
 * as AB#805's reassignment (any member site; every other site closed) only with
 * AmsAvailabilityWeekView on; with it off, as before: main office to outreach site, main offices
 * closed. The page itself is retired once the flag is on, so this is almost always the second.
 */
export function reassignmentWording(weekViewOn: boolean): {
  kindLabel: string
  kindDescription: string
  targetLabel: string
  awayAllLabel: string
} {
  return weekViewOn
    ? {
        kindLabel: KIND_LABELS[0],
        kindDescription: KIND_DESCRIPTIONS[0],
        targetLabel: "Site they go to",
        awayAllLabel: "Every other site they host at",
      }
    : {
        kindLabel: "Outreach site",
        kindDescription: "A host works an outreach site instead of their main office — closed at the main office, open at the outreach site.",
        targetLabel: "Outreach site",
        awayAllLabel: "Every main office they host at",
      }
}

/** An override kind's label as the Schedule Overrides page and the detail dialog show it (kind 0 per reassignmentWording). */
export function kindLabel(kind: OverrideKind, weekViewOn: boolean): string {
  return kind === KIND.outreachAssignment ? reassignmentWording(weekViewOn).kindLabel : KIND_LABELS[kind]
}

// ── The form ─────────────────────────────────────────────────────────

/** Who an event-type override applies to. */
export type EventTypeScope = "site" | "hostAtSite" | "host"

export interface OverrideFormState {
  kind: OverrideKind | null
  /** "yyyy-MM-ddTHH:mm" in the sites' zone, as a datetime-local input holds it. */
  start: string
  /** The same. 11:59 PM stands for the end of the day. */
  end: string
  teamSlug: string
  outreachTeamSlug: string
  hostEmail: string
  eventTypeSlug: EventTypeSlug | ""
  scope: EventTypeScope
  note: string
}

/** Midnight and 11:59 PM: a new override covers whole days unless told otherwise. */
export const DAY_START = "00:00"
export const DAY_END = "23:59"

export function emptyForm(today: string): OverrideFormState {
  return {
    kind: null,
    start: `${today}T${DAY_START}`,
    end: `${today}T${DAY_END}`,
    teamSlug: "",
    outreachTeamSlug: "",
    hostEmail: "",
    eventTypeSlug: "",
    scope: "site",
    note: "",
  }
}

export type FieldRule = "required" | "optional" | "hidden"

export interface FormFields {
  /** The site field. For the host kinds an empty value means "every site they host at". */
  site: FieldRule
  /** What the site field is called for this kind. */
  siteLabel: string
  /** What an empty optional site means. */
  siteAllLabel: string
  outreachSite: FieldRule
  host: FieldRule
  eventType: FieldRule
  /** Whether to ask who an event-type override applies to. */
  scope: boolean
  note: FieldRule
}

const HIDDEN: FormFields = {
  site: "hidden",
  siteLabel: "Site",
  siteAllLabel: "",
  outreachSite: "hidden",
  host: "hidden",
  eventType: "hidden",
  scope: false,
  note: "hidden",
}

/**
 * Which fields a kind asks for. Mirrors the server's rules in ScheduleOverridePlanner; `weekViewOn`
 * (AmsAvailabilityWeekView) changes only what a reassignment's "all sites" choice is called.
 */
export function fieldsFor(state: Pick<OverrideFormState, "kind" | "scope">, weekViewOn = false): FormFields {
  switch (state.kind) {
    case KIND.outreachAssignment:
      return {
        ...HIDDEN,
        host: "required",
        outreachSite: "required",
        site: "optional",
        siteLabel: "Away from",
        siteAllLabel: reassignmentWording(weekViewOn).awayAllLabel,
        note: "optional",
      }
    case KIND.outOfOffice:
      return {
        ...HIDDEN,
        host: "required",
        site: "optional",
        siteLabel: "Site",
        siteAllLabel: "Every site they host at",
        note: "required",
      }
    case KIND.siteClosure:
      return { ...HIDDEN, site: "required", note: "optional" }
    case KIND.eventTypeOverride:
      return {
        ...HIDDEN,
        scope: true,
        eventType: "required",
        site: state.scope === "host" ? "hidden" : "required",
        host: state.scope === "site" ? "hidden" : "required",
        note: "optional",
      }
    default:
      return HIDDEN
  }
}

/** What stops the form being sent at all. The server's refusals come back through the preview. */
export function clientErrors(state: OverrideFormState, today: string): string[] {
  if (state.kind === null) return ["Choose what kind of override this is."]

  const fields = fieldsFor(state)
  const errors: string[] = []

  // Fixed-width local date-times, so comparing the strings compares the moments.
  if (!state.start || !state.end) errors.push("Choose when it starts and ends.")
  else if (state.end <= state.start) errors.push("The end is not after the start.")
  else if (state.start.slice(0, 10) < today) errors.push("An override cannot start in the past.")

  if (fields.host === "required" && !state.hostEmail) errors.push("Choose a host.")
  if (fields.site === "required" && !state.teamSlug) errors.push("Choose a site.")
  if (fields.outreachSite === "required" && !state.outreachTeamSlug) errors.push("Choose the outreach site.")
  if (fields.eventType === "required" && !state.eventTypeSlug) errors.push("Choose which event type to run.")
  if (fields.note === "required" && !state.note.trim()) errors.push("Add a note saying why.")

  return errors
}

/** The request, carrying only the fields this kind uses. */
export function buildRequest(state: OverrideFormState): CreateScheduleOverrideRequest | null {
  if (state.kind === null) return null

  const fields = fieldsFor(state)
  const note = state.note.trim()

  return {
    kind: state.kind,
    startDate: state.start.slice(0, 10),
    startTime: `${state.start.slice(11, 16)}:00`,
    endDate: state.end.slice(0, 10),
    endTime: `${state.end.slice(11, 16)}:00`,
    ...(fields.site !== "hidden" && state.teamSlug ? { teamSlug: state.teamSlug } : {}),
    ...(fields.outreachSite !== "hidden" && state.outreachTeamSlug ? { outreachTeamSlug: state.outreachTeamSlug } : {}),
    ...(fields.host !== "hidden" && state.hostEmail ? { hostEmail: state.hostEmail } : {}),
    ...(fields.eventType !== "hidden" && state.eventTypeSlug ? { eventTypeSlug: state.eventTypeSlug } : {}),
    ...(fields.note !== "hidden" && note ? { note } : {}),
  }
}

// ── Describing an override in the list ───────────────────────────────

/** Who or where it is about, in a line. */
export function describeTarget(o: ScheduleOverrideSummary): string {
  const host = o.hostName ?? o.hostEmail
  const site = o.teamName ?? o.teamSlug

  switch (o.kind) {
    case KIND.outreachAssignment:
      return `${host ?? "Host"} at ${o.outreachTeamName ?? o.outreachTeamSlug ?? "an outreach site"}`
    case KIND.outOfOffice:
      return site ? `${host ?? "Host"} at ${site}` : `${host ?? "Host"} (all sites)`
    case KIND.siteClosure:
      return site ?? "Site"
    case KIND.eventTypeOverride:
      if (host && site) return `${host} at ${site}`
      if (host) return `${host} (all sites)`
      return site ?? "Site"
  }
}

/** The kind-specific detail — which event type runs, or where the host is away from. */
export function describeEffect(o: ScheduleOverrideSummary): string | null {
  switch (o.kind) {
    case KIND.outreachAssignment:
      return `Away from ${o.teamName ?? o.teamSlug ?? "every main office"}`
    case KIND.eventTypeOverride:
      return o.eventTypeSlug ? `${EVENT_TYPE_LABELS[o.eventTypeSlug]} only` : null
    default:
      return null
  }
}

/** "Mon, Oct 5" or "Mon, Oct 5 – Fri, Oct 9", from "yyyy-MM-dd" dates read as calendar days. */
export function formatDateRange(start: string, end: string): string {
  const label = (date: string) =>
    new Date(`${date}T12:00:00Z`).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    })

  return start === end ? label(start) : `${label(start)} – ${label(end)}`
}

/** "1:00 PM", from "HH:mm" or "HH:mm:ss". */
export function formatTime(time: string): string {
  const hours = Number(time.slice(0, 2))
  const minutes = Number(time.slice(3, 5))
  const suffix = hours < 12 ? "AM" : "PM"

  return `${hours % 12 === 0 ? 12 : hours % 12}:${String(minutes).padStart(2, "0")} ${suffix}`
}

/**
 * "Mon, Oct 5, 12:00 AM – 11:59 PM", or "Mon, Oct 5, 1:00 PM – Wed, Oct 7, 10:00 AM" across days.
 * Dates are "yyyy-MM-dd" read as calendar days; times "HH:mm" or "HH:mm:ss".
 */
export function formatWindow(startDate: string, startTime: string, endDate: string, endTime: string): string {
  const start = `${formatDateRange(startDate, startDate)}, ${formatTime(startTime)}`

  return startDate === endDate
    ? `${start} – ${formatTime(endTime)}`
    : `${start} – ${formatDateRange(endDate, endDate)}, ${formatTime(endTime)}`
}

/** Days in an inclusive range. */
export function dayCount(start: string, end: string): number {
  const ms = Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)
  return Math.round(ms / 86_400_000) + 1
}
